import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { StatusBadge } from "../common/StatusBadge";
import { Loans } from "../../lib/api";
import { formatINR, formatIndianDate } from "../../lib/format";

const Schedule = ({ loanId }) => {
  const [s, setS] = useState(null);
  useEffect(() => { Loans.schedule(loanId).then(setS).catch(() => setS(false)); }, [loanId]);
  if (s === null) return <p className="text-xs text-emerald-900/50">Loading schedule…</p>;
  if (!s) return <p className="text-xs text-rose-700">Could not load schedule.</p>;
  return (
    <div data-testid="loan-schedule">
      <div className="mb-2 grid grid-cols-3 gap-2 text-xs">
        <div>Received<div className="font-mono-tabular font-bold">{formatINR(s.total_received)}</div></div>
        <div>Outstanding<div className="font-mono-tabular font-bold" data-testid="loan-schedule-outstanding">{formatINR(s.outstanding_total)}</div></div>
        <div>Overdue<div className="font-mono-tabular font-bold text-rose-700">{formatINR(s.overdue_amount)}</div></div>
      </div>
      <div className="max-h-64 overflow-y-auto rounded-lg border border-emerald-950/10">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-emerald-50 text-left text-emerald-900/70"><tr>
            <th className="px-2 py-1.5">#</th><th className="px-2 py-1.5">Due</th><th className="px-2 py-1.5 text-right">EMI</th>
            <th className="px-2 py-1.5 text-right">Paid</th><th className="px-2 py-1.5">Status</th></tr></thead>
          <tbody className="divide-y divide-emerald-950/5">
            {s.instalments.map((i) => (
              <tr key={i.no}><td className="px-2 py-1.5">{i.no}</td><td className="px-2 py-1.5 font-mono">{formatIndianDate(i.due_date)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{formatINR(i.due)}</td><td className="px-2 py-1.5 text-right font-mono">{formatINR(i.paid)}</td>
                <td className="px-2 py-1.5"><StatusBadge status={i.status} /></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const LoanViewDialog = ({ loan, names, onOpenChange }) => {
  if (!loan) return null;
  const rows = [
    ["CLF", names.clf[loan.clf_id]], ["VO", names.vo[loan.vo_id]], ["SHG", names.shg[loan.shg_id]],
    ["Borrower", loan.member_id ? names.member[loan.member_id] : "SHG (group loan)"], ["Purpose", loan.purpose || "—"],
    ["Principal", formatINR(loan.principal)], ["Disbursed on", formatIndianDate(loan.disbursement_date)],
    ["Interest", `${loan.interest_rate}% p.a. · ${loan.interest_method === "flat" ? "Flat" : "Reducing balance"}`],
    ["Tenure", `${loan.tenure_months} months`], ["Monthly EMI", formatINR(loan.emi)],
    ["Total interest", formatINR(loan.total_interest)], ["Total payable", formatINR(loan.total_payable)],
    ["First EMI due", formatIndianDate(loan.first_emi_date)], ["Status", loan.status],
    ["Created", formatIndianDate(loan.created_at)], ["Last updated", formatIndianDate(loan.updated_at)],
  ];
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto bg-white" data-testid="loan-view-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-emerald-950">{loan.loan_number}</DialogTitle>
          <DialogDescription>Saved loan record</DialogDescription>
        </DialogHeader>
        <dl className="divide-y divide-emerald-950/5 text-sm">
          {rows.map(([k, val]) => (
            <div key={k} className="flex justify-between gap-4 py-2">
              <dt className="text-emerald-900/60">{k}</dt>
              <dd className="text-right font-medium capitalize text-emerald-950">{val || "—"}</dd>
            </div>
          ))}
        </dl>
        <h4 className="mt-2 font-display text-sm font-semibold text-emerald-950">Repayment schedule</h4>
        <Schedule loanId={loan.id} />
      </DialogContent>
    </Dialog>
  );
};
